// filepath: c:\Users\barto\Desktop\invayt\web-app\src\components\ui\button.tsx
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/shadcn"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        primary: "bg-primary/10 text-primary hover:bg-primary/20",
        dark: "bg-gray-900 text-white hover:bg-gray-700",
        approved: "bg-green-100 text-green-700 hover:bg-green-200",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outlinePrimary:
          "border border-primary bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/70",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        "outline-selected":
          "border border-primary bg-background ",
        "outline-approve":
          "border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 hover:border-green-300",
        "outline-deny":
          "border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        brandSecondary:
          "bg-accent text-accent-foreground hover:bg-accent/90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        primaryGhost:
          "text-primary hover:bg-primary hover:text-primary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  variant?: "outlinePrimary" | "primaryGhost" | "approved" | "default" | "destructive" | "outline" | "secondary" | "brandSecondary" | "ghost" | "link" | "primary" | "dark" | "outline-selected" | "outline-approve" | "outline-deny"
  size?: "default" | "sm" | "lg" | "icon"
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }